import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EmailComposerModal = ({ isOpen, onClose, selectedApplications, onSend }) => {
  const [emailData, setEmailData] = useState({
    template: 'approval',
    subject: 'Ihre THG-Prämie wurde genehmigt',
    body: `Sehr geehrte/r {{firstname}} {{lastname}},\n\nwir freuen uns, Ihnen mitteilen zu können, dass Ihr THG-Prämienantrag genehmigt wurde.\n\nIhre Details:\nFahrgestellnummer: {{chassisnumber}}\nGutscheincode: {{vouchercode}}\n\nMit freundlichen Grüßen\nIhr Grid Balance Team`
  });
  const [isSending, setIsSending] = useState(false);

  if (!isOpen) return null;

  const templateOptions = [
    { value: 'approval', label: 'Genehmigungsbestätigung' },
    { value: 'rejection', label: 'Ablehnungsbenachrichtigung' },
    { value: 'reminder', label: 'Erinnerung' },
    { value: 'custom', label: 'Benutzerdefiniert' }
  ];

  const placeholders = [
    { value: '{{firstname}}', label: 'Vorname' },
    { value: '{{lastname}}', label: 'Nachname' },
    { value: '{{chassisnumber}}', label: 'Fahrgestellnummer' },
    { value: '{{vouchercode}}', label: 'Gutscheincode' },
    { value: '{{dealership}}', label: 'Partner' },
    { value: '{{date}}', label: 'Datum' }
  ];

  const handleTemplateChange = (template) => {
    const templates = {
      approval: {
        subject: 'Ihre THG-Prämie wurde genehmigt',
        body: `Sehr geehrte/r {{firstname}} {{lastname}},\n\nwir freuen uns, Ihnen mitteilen zu können, dass Ihr THG-Prämienantrag genehmigt wurde.\n\nIhre Details:\nFahrgestellnummer: {{chassisnumber}}\nGutscheincode: {{vouchercode}}\n\nMit freundlichen Grüßen\nIhr Grid Balance Team`
      },
      rejection: {
        subject: 'Ihr THG-Prämienantrag',
        body: `Sehr geehrte/r {{firstname}} {{lastname}},\n\nleider müssen wir Ihnen mitteilen, dass Ihr THG-Prämienantrag nicht genehmigt werden konnte.\n\nBei Fragen kontaktieren Sie uns bitte.\n\nMit freundlichen Grüßen\nIhr Grid Balance Team`
      },
      reminder: {
        subject: 'Erinnerung: Fehlende Unterlagen',
        body: `Sehr geehrte/r {{firstname}} {{lastname}},\n\nfür die Bearbeitung Ihres Antrags benötigen wir noch weitere Unterlagen.\n\nBitte reichen Sie diese zeitnah nach.\n\nMit freundlichen Grüßen\nIhr Grid Balance Team`
      },
      custom: {
        subject: '',
        body: ''
      }
    };

    setEmailData({
      template,
      ...templates?.[template]
    });
  };

  const insertPlaceholder = (placeholder) => {
    setEmailData(prev => ({
      ...prev,
      body: prev?.body + placeholder
    }));
  };

  const handleSend = async () => {
    setIsSending(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    onSend(emailData);
    setIsSending(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 animate-fade-in">
      <div className="bg-card rounded-lg shadow-modal max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-2xl font-heading font-semibold text-foreground">
              E-Mail versenden
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              An {selectedApplications?.length} {selectedApplications?.length === 1 ? 'Empfänger' : 'Empfänger'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-smooth"
            aria-label="Schließen"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <Select
            label="E-Mail-Vorlage"
            options={templateOptions}
            value={emailData?.template}
            onChange={handleTemplateChange}
          />

          <Input
            label="Betreff"
            type="text"
            value={emailData?.subject}
            onChange={(e) => setEmailData(prev => ({ ...prev, subject: e?.target?.value }))}
            placeholder="E-Mail-Betreff eingeben"
          />

          <div className="space-y-2">
            <label className="block text-sm font-medium text-foreground">
              Nachricht
            </label>
            <textarea
              value={emailData?.body}
              onChange={(e) => setEmailData(prev => ({ ...prev, body: e?.target?.value }))}
              rows={10}
              className="w-full px-4 py-3 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-smooth resize-none"
              placeholder="E-Mail-Nachricht eingeben"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {placeholders?.map((placeholder) => (
                <button
                  key={placeholder?.value}
                  onClick={() => insertPlaceholder(placeholder?.value)}
                  className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 text-foreground rounded-full transition-smooth"
                >
                  {placeholder?.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  Platzhalter-Variablen
                </p>
                <p className="text-xs text-muted-foreground">
                  Verwenden Sie die Platzhalter-Buttons, um personalisierte Variablen einzufügen. Diese werden automatisch mit den entsprechenden Kundendaten ersetzt.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} iconName="">
            Abbrechen
          </Button>
          <Button
            variant="default"
            onClick={handleSend}
            loading={isSending}
            iconName="Send"
            iconPosition="left"
          >
            E-Mails senden
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposerModal;